import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Command, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';

interface MentionUser {
  id: number;
  name: string;
  profilePictureUrl?: string;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  onSubmit?: () => void;
  className?: string;
}

export function MentionInput({
  value,
  onChange,
  placeholder = 'Share your thoughts... @ to mention',
  disabled = false,
  onSubmit,
  className,
}: MentionInputProps) {
  const { user } = useAuthStore();
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<MentionUser[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const mentionPattern = useMemo(() => /@(\w*)$/, []);

  useEffect(() => {
    if (mentionStartIndex !== null && mentionQuery.length >= 0) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        try {
          const response = await api.get<{ users: MentionUser[] }>(
            `users/search?query=${encodeURIComponent(mentionQuery)}&pageSize=10`
          );
          if (response.success && response.data) {
            const filtered = response.data.users.filter(u => u.id !== user?.id);
            setSearchResults(filtered);
            setSelectedIndex(0);
          }
        } catch (error) {
          console.error('Failed to search users:', error);
          setSearchResults([]);
        }
      }, 300);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [mentionQuery, mentionStartIndex, user?.id]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    const cursorPosition = e.target.selectionStart || 0;
    const textBeforeCursor = newValue.slice(0, cursorPosition);
    const match = textBeforeCursor.match(mentionPattern);

    if (match) {
      const startIndex = cursorPosition - match[1].length;
      setMentionStartIndex(startIndex);
      setMentionQuery(match[1]);
      setIsPopoverOpen(true);
    } else {
      setMentionStartIndex(null);
      setMentionQuery('');
      setIsPopoverOpen(false);
    }
  }, [onChange, mentionPattern]);

  const insertMention = useCallback((selectedUser: MentionUser) => {
    if (mentionStartIndex === null) return;

    const cursorPosition = inputRef.current?.selectionStart || 0;
    const textBeforeCursor = value.slice(0, cursorPosition);
    const textAfterCursor = value.slice(cursorPosition);

    const newTextBefore = textBeforeCursor.replace(mentionPattern, `@${selectedUser.name} `);
    const newValue = newTextBefore + textAfterCursor;

    onChange(newValue);
    setIsPopoverOpen(false);
    setMentionStartIndex(null);
    setMentionQuery('');

    setTimeout(() => {
      if (inputRef.current) {
        const newPosition = newTextBefore.length;
        inputRef.current.setSelectionRange(newPosition, newPosition);
        inputRef.current.focus();
      }
    }, 0);
  }, [mentionStartIndex, value, onChange, mentionPattern]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isPopoverOpen || searchResults.length === 0) {
      if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
        e.preventDefault();
        onSubmit();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % searchResults.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + searchResults.length) % searchResults.length);
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        if (searchResults[selectedIndex]) {
          insertMention(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsPopoverOpen(false);
        setMentionStartIndex(null);
        break;
    }
  }, [isPopoverOpen, searchResults, selectedIndex, insertMention, onSubmit]);

  return (
    <div className={className}>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverAnchor asChild>
          <Input
            ref={inputRef}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full"
          />
        </PopoverAnchor>
        <PopoverContent
          className="p-0"
          side="top"
          align="start"
          sideOffset={4}
          style={{ width: 'var(--radix-popover-content-width)' }}
        >
          <Command className="w-full">
            <CommandEmpty>
              {mentionQuery ? (
                <p className="py-4 text-sm text-muted-foreground text-center">
                  No users found for "{mentionQuery}"
                </p>
              ) : (
                <p className="py-4 text-sm text-muted-foreground text-center">
                  Type to search for users
                </p>
              )}
            </CommandEmpty>
            <CommandGroup>
              {searchResults.map((user, index) => (
                <CommandItem
                  key={user.id}
                  value={`${user.name} ${user.id}`}
                  onSelect={() => insertMention(user)}
                  className={`cursor-pointer ${index === selectedIndex ? 'bg-accent' : ''}`}
                >
                  <Avatar className="h-6 w-6 mr-2">
                    <AvatarImage src={user.profilePictureUrl} />
                    <AvatarFallback className="text-xs">
                      {user.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span>{user.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}